import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  MousePointer,
  Globe,
  Smartphone,
  TrendingUp,
  Calendar,
  RefreshCw,
  Monitor
} from 'lucide-react';
import { useSiteContext } from '../contexts/SiteContext';

const AnalyticsDashboard = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [websiteInfo, setWebsiteInfo] = useState(null);
  const [websiteLoading, setWebsiteLoading] = useState(false);
  const [websiteError, setWebsiteError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [activeTab, setActiveTab] = useState('overview');

  // For countries tab - selected country and cities data
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [citiesData, setCitiesData] = useState(null);
  const [citiesLoading, setCitiesLoading] = useState(false);

  // For devices tab - sub-tabs
  const [deviceSubTab, setDeviceSubTab] = useState('device_types');

  useEffect(() => {
    if (selectedSite) {
      fetchAnalyticsData();
      fetchWebsiteInfo();
    } else {
      setLoading(false);
      setError('Please select a site to view analytics');
    }
  }, [user, selectedSite, timeRange, activeTab, deviceSubTab]);

  // Reset selected country when switching away from countries tab
  useEffect(() => {
    if (activeTab !== 'countries') {
      setSelectedCountry(null);
      setCitiesData(null);
    }
  }, [activeTab]);

  const getDateRange = () => {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date();
    switch (timeRange) {
      case '1d':
        startDate.setDate(startDate.getDate() - 1);
        break;
      case '7d':
        startDate.setDate(startDate.getDate() - 7);
        break;
      case '30d':
        startDate.setDate(startDate.getDate() - 30);
        break;
      case '90d':
        startDate.setDate(startDate.getDate() - 90);
        break;
      default:
        startDate.setDate(startDate.getDate() - 7);
    }
    return { startDate: startDate.toISOString().split('T')[0], endDate };
  };

  const fetchAnalyticsData = async () => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = getDateRange();

      // For devices tab, use the sub-tab type
      const type = activeTab === 'devices' ? deviceSubTab : activeTab;

      const response = await fetch(
        `/app/api/analytics-portal/statistics.php?site_id=${selectedSite.id}&type=${type}&start_date=${startDate}&end_date=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setAnalyticsData(result.data);
      } else {
        setError(result.message || 'Failed to fetch analytics data');
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCitiesForCountry = async (countryCode, countryName) => {
    if (!selectedSite || !countryCode) return;

    setSelectedCountry({ code: countryCode, name: countryName });
    setCitiesLoading(true);
    setCitiesData(null);

    try {
      const { startDate, endDate } = getDateRange();

      const response = await fetch(
        `/app/api/analytics-portal/statistics.php?site_id=${selectedSite.id}&type=cities&country_code=${countryCode}&start_date=${startDate}&end_date=${endDate}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setCitiesData(result.data);
      }
    } catch (error) {
      console.error('Error fetching cities data:', error);
    } finally {
      setCitiesLoading(false);
    }
  };

  const fetchWebsiteInfo = async () => {
    if (!selectedSite) return;

    setWebsiteLoading(true);
    setWebsiteError(null);

    try {
      const resp = await fetch(`/app/api/analytics-portal/website.php?site_id=${selectedSite.id}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      const res = await resp.json();
      if (resp.ok && res.success) {
        setWebsiteInfo(res.data);
      } else {
        setWebsiteError(res.message || 'Failed to fetch website info');
      }
    } catch (err) {
      console.error('Error fetching website info:', err);
      setWebsiteError('Network error. Please try again.');
    } finally {
      setWebsiteLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat().format(num);
  };

  if (!selectedSite) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <BarChart3 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Site Selected</h2>
            <p className="text-gray-500">Please select a site from the dropdown to view analytics data.</p>
          </div>
        </div>
      </div>
    );
  }

  const renderCountriesTab = () => {
    if (!analyticsData?.data) return null;

    return (
      <div className="flex gap-6">
        {/* Countries List - Left Half */}
        <div className="w-1/2 bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h3 className="font-medium text-gray-700">Countries</h3>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visitors
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analyticsData.data.map((item, index) => {
                  const countryCode = item.country_code ? item.country_code.toLowerCase() : '';
                  const countryName = item.country_name || 'Unknown';
                  const isSelected = selectedCountry?.code === item.country_code;

                  return (
                    <tr
                      key={index}
                      className={`cursor-pointer transition ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      onClick={() => fetchCitiesForCountry(item.country_code, countryName)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          {countryCode && (
                            <img
                              src={`https://mageguard.net/analytics/themes/altum/assets/images/countries/${countryCode}.svg`}
                              alt=""
                              className="w-4 h-4"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>{countryName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(item.visitors)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Cities List - Right Half */}
        <div className="w-1/2 bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b">
            <h3 className="font-medium text-gray-700">
              {selectedCountry ? `Cities in ${selectedCountry.name}` : 'Select a country to view cities'}
            </h3>
          </div>
          {citiesLoading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 text-blue-600 animate-spin mx-auto mb-2" />
              <span className="text-gray-500 text-sm">Loading cities...</span>
            </div>
          ) : citiesData?.data && citiesData.data.length > 0 ? (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      City
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visitors
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {citiesData.data.map((item, index) => {
                    const cityName = item.city_name || item.city || 'Unknown';
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {cityName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(item.visitors)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : selectedCountry ? (
            <div className="p-8 text-center text-gray-500">
              No city data available for {selectedCountry.name}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">
              Click on a country to view its cities
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDevicesTab = () => {
    return (
      <div className="space-y-4">
        {/* Sub-tabs */}
        <div className="bg-white rounded-lg shadow-md p-3">
          <div className="flex gap-2">
            {[
              { id: 'device_types', label: 'Devices' },
              { id: 'screen_resolutions', label: 'Screen Resolutions' },
              { id: 'browser_languages', label: 'Languages' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setDeviceSubTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm transition ${
                  deviceSubTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Data table */}
        {analyticsData?.data && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {deviceSubTab === 'device_types' ? 'Device Type' :
                       deviceSubTab === 'screen_resolutions' ? 'Screen Resolution' :
                       'Language'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visitors
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analyticsData.data.map((item, index) => {
                    let displayValue = 'Unknown';
                    let iconUrl = null;

                    if (deviceSubTab === 'device_types') {
                      const deviceType = item.device_type || 'unknown';
                      displayValue = deviceType;
                      iconUrl = `https://mageguard.net/analytics/themes/altum/assets/images/device_types/${deviceType.toLowerCase()}.svg`;
                    } else if (deviceSubTab === 'screen_resolutions') {
                      displayValue = item.screen_resolution || 'Unknown';
                    } else if (deviceSubTab === 'browser_languages') {
                      displayValue = item.browser_language || 'Unknown';
                    }

                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            {iconUrl && (
                              <img
                                src={iconUrl}
                                alt=""
                                className="w-4 h-4"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <span>{displayValue}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatNumber(item.visitors)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSimpleTable = (labelKey, labelHeader, iconType = null) => {
    if (!analyticsData?.data) return null;

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {labelHeader}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visitors
                </th>
                {analyticsData.data[0]?.bounces !== undefined && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bounces
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analyticsData.data.map((item, index) => {
                let displayValue = item[labelKey] || 'Unknown';
                let iconUrl = null;

                if (iconType === 'browser') {
                  const browserName = item.browser_name || item.name || 'unknown';
                  displayValue = browserName;
                  iconUrl = `https://mageguard.net/analytics/themes/altum/assets/images/browsers/${browserName.toLowerCase()}.svg`;
                } else if (iconType === 'os') {
                  const osName = item.os_name || 'unknown';
                  displayValue = osName;
                  iconUrl = `https://mageguard.net/analytics/themes/altum/assets/images/os/${osName.toLowerCase()}.svg`;
                } else if (iconType === 'referrer') {
                  const referrerHost = item.referrer_host || 'Unknown';
                  displayValue = referrerHost;
                  if (referrerHost !== 'Unknown') {
                    iconUrl = `https://external-content.duckduckgo.com/ip3/${referrerHost}.ico`;
                  }
                }

                // Trim long URLs
                if (labelKey === 'path' && displayValue.length > 60) {
                  displayValue = displayValue.substring(0, 57) + '...';
                }

                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        {iconUrl && (
                          <img
                            src={iconUrl}
                            alt=""
                            className="w-4 h-4"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span title={item[labelKey]}>{displayValue}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.visitors)}
                    </td>
                    {item.bounces !== undefined && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(item.bounces)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPathsTable = () => {
    if (!analyticsData?.data) return null;

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Page Path
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pageviews
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Visitors
                </th>
                {analyticsData.data[0]?.bounces !== undefined && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bounces
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analyticsData.data.map((item, index) => {
                const path = item.path || 'Unknown';
                const displayPath = path.length > 60 ? path.substring(0, 57) + '...' : path;

                return (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <span title={path}>{displayPath}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.pageviews)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(item.visitors)}
                    </td>
                    {item.bounces !== undefined && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatNumber(item.bounces)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Analytics Dashboard</h1>
            <p className="text-gray-600">Viewing analytics for: <span className="font-semibold">{selectedSite.name}</span></p>
            {/* Pixel embed code display (uses pixel_key from analytics portal) */}
            {!websiteLoading && (
              (() => {
                const pixelKey = websiteInfo?.data?.pixel_key || websiteInfo?.pixel_key;
                if (!pixelKey) return null;
                const code = `<!-- Pixel Code - https://mageguard.net/analytics/ -->\n<script defer src="https://mageguard.net/analytics/pixel/${pixelKey}"></script>\n<!-- END Pixel Code -->`;
                return (
                  <div className="mt-3">
                    <p className="text-sm text-gray-600 mb-1">Pixel embed code</p>
                    <div className="bg-gray-100 rounded-md p-3 font-mono text-sm text-gray-800 flex items-start justify-between gap-4">
                      <pre className="whitespace-pre-wrap break-all mr-4">{code}</pre>
                      <div className="flex flex-col items-end gap-2">
                        <button
                          onClick={() => { if (navigator.clipboard) navigator.clipboard.writeText(code); }}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Copy
                        </button>
                        <a
                          href={`https://mageguard.net/analytics/pixel/${pixelKey}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-gray-500 underline"
                        >
                          Open pixel URL
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })()
            )}
            {websiteLoading && (
              <div className="mt-3 text-sm text-gray-500">Loading site info...</div>
            )}
            {websiteError && (
              <div className="mt-3 text-sm text-red-600">{websiteError}</div>
            )}
          </div>
          <button
            onClick={fetchAnalyticsData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Time Range Selector */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <div className="flex gap-2">
            {['1d', '7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2 rounded-lg transition ${
                  timeRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {range === '1d' ? 'Last 24 Hours' : range === '7d' ? 'Last 7 Days' : range === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6 bg-white rounded-lg shadow-md p-4">
          <div className="flex gap-2 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'paths', label: 'Pages', icon: MousePointer },
              { id: 'referrers', label: 'Referrers', icon: Globe },
              { id: 'countries', label: 'Countries', icon: Globe },
              { id: 'devices', label: 'Devices', icon: Smartphone },
              { id: 'browser_names', label: 'Browsers', icon: TrendingUp },
              { id: 'operating_systems', label: 'Operating Systems', icon: Monitor }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mr-3" />
              <span className="text-gray-600">Loading analytics data...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading analytics data</h3>
                <p className="mt-1 text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {activeTab === 'overview' && analyticsData?.data && (
              <div className="grid grid-cols-1 gap-6">
                {[...analyticsData.data].sort((a, b) => {
                  const dateA = a.date || '';
                  const dateB = b.date || '';
                  const timeA = dateA ? new Date(dateA).getTime() : 0;
                  const timeB = dateB ? new Date(dateB).getTime() : 0;
                  return timeB - timeA;
                }).map((item, index) => (
                  <div key={index} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center justify-between mb-4">
                      <Calendar className="w-8 h-8 text-blue-600" />
                      <span className="text-sm text-gray-500">{item.formatted_date}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Pageviews</p>
                        <p className="text-2xl font-bold text-gray-800">{formatNumber(item.pageviews)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Visitors</p>
                        <p className="text-2xl font-bold text-gray-800">{formatNumber(item.visitors)}</p>
                      </div>
                      {item.sessions && (
                        <div>
                          <p className="text-sm text-gray-600">Sessions</p>
                          <p className="text-2xl font-bold text-gray-800">{formatNumber(item.sessions)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'paths' && renderPathsTable()}

            {activeTab === 'referrers' && renderSimpleTable('referrer_host', 'Referrer', 'referrer')}

            {activeTab === 'countries' && renderCountriesTab()}

            {activeTab === 'devices' && renderDevicesTab()}

            {activeTab === 'browser_names' && renderSimpleTable('browser_name', 'Browser', 'browser')}

            {activeTab === 'operating_systems' && renderSimpleTable('os_name', 'Operating System', 'os')}

            {analyticsData?.data && analyticsData.data.length === 0 && (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <BarChart3 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No Data Available</h3>
                <p className="text-gray-500">No analytics data found for the selected time range.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
